/**
 * Customer module used to manage User related data mainly stored in cookies
 * Different 3rd party services has different cookies to store user data, like DynamicYield, Iterable, Rudderstack, etc
 * This module helps to consolidate all User data in one place
 *
 */

import {
  CUSTOMER_ITERABLE,
  CUSTOMER_TRACKING_COOKIES,
  CUSTOMER_USER_STATES_COOKIES,
  EXPIRATION_COOKIES,
} from '~/constants/cookies';
import * as DataDogLogs from '~/lib/datadog/datadog';
import * as CookiesUtils from '~/utils/cookies';
import * as CypherUtils from '~/utils/cypher';
import * as HashUtils from '~/utils/hash';
import * as UrlUtils from '~/utils/url';

/**
 * Get the user's encrypted email from a Cookie
 *
 * @param {string} email
 */
const encryptedEmail = () =>
  CookiesUtils.getCookie(CUSTOMER_TRACKING_COOKIES.ALLBIRDS_ENCRYPTED_EMAIL);

/**
 * Get the user's state from a Cookie
 *
 * @returns {string} user state
 */
const userState = () => CookiesUtils.getCookie(CUSTOMER_TRACKING_COOKIES.USER_STATE);

/**
 * Get the user's dynamic yield server ID from a Cookie
 *
 * @returns {string} dynamic yield server ID
 */
const dynamicYieldServerID = () =>
  CookiesUtils.getCookie(CUSTOMER_TRACKING_COOKIES.DYNAMIC_YIELD_SERVER);

/**
 * Get the user's dynamic yield client ID from a Cookie
 *
 * @returns {stricttring} dynamic yield client ID
 */
const dynamicYieldClientID = () => CookiesUtils.getCookie(CUSTOMER_TRACKING_COOKIES.DYNAMIC_YIELD);

/**
 * Get the user's allbirds ID from a Cookie
 *
 * @returns {string} allbirds ID
 */
const allbirdsId = () => CookiesUtils.getCookie(CUSTOMER_TRACKING_COOKIES.ALLBIRDS_ID);

/**
 * Get the user's ID from a Cookie
 *
 * @returns {string} user ID
 */
const userId = () => CookiesUtils.getCookie(CUSTOMER_TRACKING_COOKIES.USER_ID);

/**
 * Get the user's preferred store from a Cookie
 *
 * @returns {string} preferred store
 */
const preferredStore = () => CookiesUtils.getCookie(CUSTOMER_TRACKING_COOKIES.PREFERRED_STORE);

/**
 * Provides rudderstack anonymousId
 *
 * @returns {string} Rudderstack anonymousId
 */
const anonymousId = () => window?.rudderanalytics?.getAnonymousId() || null;

/**
 * Get the user's session replay session ID from a Cookie
 *
 * @returns {string} session replay session ID
 */
const sessionReplaySessionId = () =>
  CookiesUtils.getCookie(CUSTOMER_TRACKING_COOKIES.SESSION_REPLAY_SESSION_ID);

/**
 * Set the user's session replay session ID in a Cookie
 *
 * @param {string} sessionId
 * @returns {void}
 */
const setSessionReplaySessionId = (sessionId) =>
  CookiesUtils.setCookie(CUSTOMER_TRACKING_COOKIES.SESSION_REPLAY_SESSION_ID, sessionId);

/**
 * Set the user's encrypted email in a Cookie
 *
 * @param {string} email
 * @returns {void}
 */
const setUserEncryptedEmail = (email) => {
  CookiesUtils.setCookie(CUSTOMER_TRACKING_COOKIES.ALLBIRDS_ENCRYPTED_EMAIL, email, {
    domain: UrlUtils.getCurrentDomain(),
    expires: EXPIRATION_COOKIES.ONE_YEAR,
  });
};

/**
 * Set the user's encrypted email hash256 in a Cookie for Bliss Point
 *
 * @param {string} hash256Email
 * @returns {void}
 */
const setUserEncryptedEmailBlisspoint = (hash256Email) => {
  CookiesUtils.setCookie(CUSTOMER_TRACKING_COOKIES.ALLBIRDS_BLISSPOINT_EMAIL, hash256Email, {
    domain: UrlUtils.getCurrentDomain(),
    expires: EXPIRATION_COOKIES.ONE_YEAR,
  });
};

/**
 * Set the user's state in a Cookie
 *
 * @param {string} userState
 * @returns {void}
 */
const setUserState = (userState) => {
  CookiesUtils.setCookie(CUSTOMER_TRACKING_COOKIES.USER_STATE, userState, {
    domain: UrlUtils.getCurrentDomain(),
    expires: EXPIRATION_COOKIES.ONE_YEAR,
  });
};

/**
 * Set the user's allbirds ID in a Cookie
 *
 * @param {string} allbirdsId
 * @returns {void}
 */
const setUserAllbirdsId = (allbirdsId) => {
  CookiesUtils.setCookie(CUSTOMER_TRACKING_COOKIES.ALLBIRDS_ID, allbirdsId, {
    domain: UrlUtils.getCurrentDomain(),
    expires: EXPIRATION_COOKIES.ONE_YEAR,
  });
};

/**
 * Set the user's ID in a Cookie
 *
 * @param {string} userId
 * @returns {void}
 */
const setUserId = (userId) => {
  CookiesUtils.setCookie(CUSTOMER_TRACKING_COOKIES.USER_ID, userId, {
    domain: UrlUtils.getCurrentDomain(),
    expires: EXPIRATION_COOKIES.ONE_YEAR,
  });
};

/**
 * Try to get allbirds ID from URL
 * ie: email campaign set the allbirds ID in the URL
 *
 * @returns {string} allbirds ID
 */
const tryGetAllbirdsIdFromUrl = () => {
  const allbirdsIdQueryParam = CUSTOMER_ITERABLE.ITERABLE_EMAIL_ALLBIRDS_ID_QUERY_PARAM;
  const currentQueryString = UrlUtils.parseQueryString();
  const allbirdsId = currentQueryString[allbirdsIdQueryParam];

  UrlUtils.removeQueryString([allbirdsIdQueryParam]);

  return allbirdsId;
};

/**
 * Try to set allbirds ID from URL
 * ie: email campaign set the allbirds ID in the URL
 *
 * @returns {void}
 */
const trySetAllbirdsIdFromUrl = () => {
  const customerAllbirdsId = tryGetAllbirdsIdFromUrl();
  if (customerAllbirdsId) {
    setUserAllbirdsId(customerAllbirdsId);
  }
};

/**
 * Try to get user ID from URL
 *
 * @returns {string} user ID
 */
const tryGetUserIdFromUrl = () => {
  const userIdQueryParam = CUSTOMER_ITERABLE.ITERABLE_EMAIL_USER_ID_QUERY_PARAM;
  const currentQueryString = UrlUtils.parseQueryString();
  const userId = currentQueryString[userIdQueryParam];

  return userId;
};

/**
 * Try to set user ID from URL
 *
 * @returns {void}
 */
const trySetUserIdFromUrl = () => {
  const userId = tryGetUserIdFromUrl();
  if (userId) {
    setUserId(userId);
  }
};

/**
 * Set iterable campaign ID in a Cookie
 *
 * @param {string} campaignId
 * @returns {void}
 */
const setIterableCampaignId = (campaignId) => {
  CookiesUtils.setCookie(CUSTOMER_ITERABLE.ITERABLE_CAMPAIGN_ID_COOKIE, campaignId, {
    domain: UrlUtils.getCurrentDomain(),
    expires: EXPIRATION_COOKIES.ONE_DAY,
  });
};

/**
 * Set iterable template ID in a Cookie
 *
 * @param {string} templateId
 * @returns {void}
 */
const setIterableTemplateId = (templateId) => {
  CookiesUtils.setCookie(CUSTOMER_ITERABLE.ITERABLE_TEMPLATE_ID_COOKIE, templateId, {
    domain: UrlUtils.getCurrentDomain(),
    expires: EXPIRATION_COOKIES.ONE_DAY,
  });
};

/**
 * Try to get iterable campaign ID from URL
 * ie: email campaign set the campaign ID in the URL
 *
 * @returns {string} campaign ID
 */
const tryGetIterableCampaignIdFromUrl = () => {
  const currentQueryString = UrlUtils.parseQueryString();

  return currentQueryString[CUSTOMER_ITERABLE.ITERABLE_EMAIL_CAMPAIGN_ID_QUERY_PARAM];
};

/**
 * Try to get iterable template ID from URL
 * ie: email campaign set the template ID in the URL
 *
 * @returns {string} template ID
 */
const tryGetIterableTemplateIdFromUrl = () => {
  const currentQueryString = UrlUtils.parseQueryString();

  return currentQueryString[CUSTOMER_ITERABLE.ITERABLE_EMAIL_TEMPLATE_ID_QUERY_PARAM];
};

/**
 * Try to set iterable campaign ID and template ID from URL
 * ie: email campaign set the campaign ID and template ID in the URL
 *
 * @returns {void}
 */
const trySetIterableRevenueTrackingIdsFromUrl = () => {
  const campaignId = tryGetIterableCampaignIdFromUrl();
  const templateId = tryGetIterableTemplateIdFromUrl();

  if (campaignId && templateId) {
    setIterableCampaignId(campaignId);
    setIterableTemplateId(templateId);
  }
};

/**
 * Reset user state
 * This method should be called when the user logs out
 *
 * TODO:
 * - Curently we do not have access to the logout event, so we are not calling this method
 * - This is a task when Shoipfy support this https://app.asana.com/0/1208717489402917/1209771071276442/f
 *
 * @returns {void}
 */
const clearUser = () => {
  CookiesUtils.removeCookie(CUSTOMER_TRACKING_COOKIES.ALLBIRDS_ENCRYPTED_EMAIL);
  CookiesUtils.removeCookie(CUSTOMER_TRACKING_COOKIES.ALLBIRDS_BLISSPOINT_EMAIL);
  CookiesUtils.removeCookie(CUSTOMER_TRACKING_COOKIES.ALLBIRDS_ID);
  CookiesUtils.removeCookie(CUSTOMER_TRACKING_COOKIES.USER_ID);
  CookiesUtils.removeCookie(CUSTOMER_ITERABLE.ITERABLE_CAMPAIGN_ID_COOKIE);
  CookiesUtils.removeCookie(CUSTOMER_ITERABLE.ITERABLE_TEMPLATE_ID_COOKIE);
  setUserState(CUSTOMER_USER_STATES_COOKIES.ANONYMOUS);
};

/**
 * This method is used update User state cookies when the User provides an email
 *
 * We should call this method every time the User provides an email
 *
 * @param {string} email
 * @param {string} userState
 * @returns {void}
 */
const identifyActionEmail = (email, userState = CUSTOMER_USER_STATES_COOKIES.ANONYMOUS) => {
  const currentUserState = userState;
  const encryptedEmail = CypherUtils.encryptTextData(email);
  const hash256Email = HashUtils.hashEmailBlissPoint(email);

  if (userState === CUSTOMER_USER_STATES_COOKIES.LOGGED_IN) {
    setUserState(CUSTOMER_USER_STATES_COOKIES.LOGGED_IN);
    setUserEncryptedEmail(encryptedEmail);
    setUserEncryptedEmailBlisspoint(hash256Email);
  } else if (currentUserState !== CUSTOMER_USER_STATES_COOKIES.LOGGED_IN) {
    setUserState(CUSTOMER_USER_STATES_COOKIES.IDENTIFIED);
    setUserEncryptedEmail(encryptedEmail);
    setUserEncryptedEmailBlisspoint(hash256Email);
  }
};

/**
 * This method is used to try to identify a User on page load based on:
 *  - User navigations comming from email campaigns
 *  - User logged in on Shopify
 *  - User identified on Iterable
 *
 * We should call this method every time the page loads
 *
 * @returns {void}
 */
const identifyPageloadEmail = () => {
  const currentUserState = userState();
  const currentEmail = encryptedEmail();
  const iterableEmailCookie = CookiesUtils.getCookie(CUSTOMER_ITERABLE.ITERABLE_EMAIL_COOKIE);
  const shopifyLoggedIn = window.ALLBIRDS.settings.customerAccountEmail;

  let updatedUserState;

  if (shopifyLoggedIn) {
    updatedUserState = CUSTOMER_USER_STATES_COOKIES.LOGGED_IN;

    const encryptedShopifyEmail = CypherUtils.encryptTextData(shopifyLoggedIn);

    setUserEncryptedEmail(encryptedShopifyEmail);
  } else if (currentUserState === CUSTOMER_USER_STATES_COOKIES.IDENTIFIED || currentEmail) {
    updatedUserState = CUSTOMER_USER_STATES_COOKIES.IDENTIFIED;
  } else if (iterableEmailCookie) {
    updatedUserState = CUSTOMER_USER_STATES_COOKIES.IDENTIFIED;

    const encryptedIterableEmail = CypherUtils.encryptTextData(iterableEmailCookie);

    setUserEncryptedEmail(encryptedIterableEmail);
  } else {
    updatedUserState = CUSTOMER_USER_STATES_COOKIES.ANONYMOUS;
  }

  setUserState(updatedUserState);
  trySetAllbirdsIdFromUrl();
  trySetUserIdFromUrl();
  trySetIterableRevenueTrackingIdsFromUrl();
};

/**
 * Get the anonymous user state
 *
 * @returns {string} user state
 */
const getAnonymousUserState = () => CUSTOMER_USER_STATES_COOKIES.ANONYMOUS;

/**
 * Get logged in user state
 *
 * @returns {string} user state
 */
const getLoggedInUserState = () => CUSTOMER_USER_STATES_COOKIES.LOGGED_IN;

/**
 * Get the identified user state
 *
 * @returns {string} user state
 */
const getIdentifiedUserState = () => CUSTOMER_USER_STATES_COOKIES.IDENTIFIED;

/**
 * Get the Shopify session ID from a Cookie
 *
 * As refference:
 * - https://community.shopify.com/t/how-to-get-users-unique-session-id-in-shopify-plugins/332419/2
 * - https://www.shopify.com/legal/cookies
 *
 * @returns {string} Shopify session ID
 */
const shopifySessionId = () => CookiesUtils.getCookie(CUSTOMER_TRACKING_COOKIES.SHOPIFY_SESSION_ID);

/**
 * Get the Shopify session S ID from a Cookie
 *  As refference:
 * - https://community.shopify.com/t/how-to-get-users-unique-session-id-in-shopify-plugins/332419/2
 * - https://www.shopify.com/legal/cookies
 *
 * _shopify_s	Used to identify a given browser session/shop combination.
 * Duration is 30 minute rolling expiry of last use.	30min
 *
 * @returns {string} Shopify session S ID
 */
const shopifySessionSId = () =>
  CookiesUtils.getCookie(CUSTOMER_TRACKING_COOKIES.SHOPIFY_SESSION_S_ID);

/**
 * Get the last action timestamp for an anonymous user
 *
 * @param {string} anonymousId - The anonymous ID to check
 * @returns {number|null} Timestamp of last action or null if not found
 */
const getLastActionTimestamp = (anonymousId) => {
  if (!anonymousId) return null;

  try {
    const cookieValue = CookiesUtils.getCookie(CUSTOMER_TRACKING_COOKIES.LAST_ACTION_TIMESTAMP);
    if (!cookieValue) return null;

    const timestamps = JSON.parse(cookieValue);
    return timestamps[anonymousId] ? parseInt(timestamps[anonymousId], 10) : null;
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds pixel] - Customer Utils - getLastActionTimestamp error',
      {},
      error,
    );
    return null;
  }
};

/**
 * Set the last action timestamp for an anonymous user
 *
 * @param {string} anonymousId - The anonymous ID to update
 * @param {number} timestamp - The timestamp to set
 */
const setLastActionTimestamp = (anonymousId, timestamp) => {
  if (!anonymousId) return;

  try {
    const existingValue = CookiesUtils.getCookie(CUSTOMER_TRACKING_COOKIES.LAST_ACTION_TIMESTAMP);
    const timestamps = existingValue ? JSON.parse(existingValue) : {};

    timestamps[anonymousId] = timestamp;

    CookiesUtils.setCookie(
      CUSTOMER_TRACKING_COOKIES.LAST_ACTION_TIMESTAMP,
      JSON.stringify(timestamps),
      {
        domain: UrlUtils.getCurrentDomain(),
        expires: EXPIRATION_COOKIES.ONE_YEAR, // 1 year for long-term returning user detection
      },
    );
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds pixel] - Customer Utils - setLastActionTimestamp error',
      {},
      error,
    );
  }
};

export {
  allbirdsId,
  anonymousId,
  clearUser,
  dynamicYieldClientID,
  dynamicYieldServerID,
  encryptedEmail,
  getAnonymousUserState,
  getIdentifiedUserState,
  getLastActionTimestamp,
  getLoggedInUserState,
  identifyActionEmail,
  identifyPageloadEmail,
  preferredStore,
  sessionReplaySessionId,
  setLastActionTimestamp,
  setSessionReplaySessionId,
  setUserAllbirdsId,
  setUserEncryptedEmail,
  setUserId,
  setUserState,
  shopifySessionId,
  shopifySessionSId,
  tryGetAllbirdsIdFromUrl,
  tryGetUserIdFromUrl,
  trySetAllbirdsIdFromUrl,
  trySetIterableRevenueTrackingIdsFromUrl,
  trySetUserIdFromUrl,
  userId,
  userState,
};
